import { useState } from "react";
import type { ProjectRole, Space } from "@melo/shared";
import { DISCIPLINE_LABELS } from "@melo/shared/client";
import { useTables } from "../lib/store";
import { people } from "../lib/select";
import { send } from "../lib/actions";
import { isAdmin, ROLE_HINT, ROLE_LABEL } from "../lib/access";
import { toast } from "../lib/toast";
import { Avatar, Dialog } from "./primitives";
import { Icon } from "./icons";
import { MenuButton } from "./pickers";
import { InviteDialog } from "./InviteDialog";

/** Who works on a project and in what role. Producers and admins can change it. */
export function TeamDialog({ space, onClose }: { space: Space; onClose: () => void }) {
  const t = useTables();
  const [inviting, setInviting] = useState(false);
  const members = [...t.members.values()].filter((m) => m.spaceId === space.id);
  const admins = people(t).filter((p) => p.role === "admin");
  const outside = people(t).filter((p) => p.role !== "admin" && !members.some((m) => m.actorId === p.id));
  const setRole = (actorId: string, role: ProjectRole | null) => void send("member.set", { spaceId: space.id, actorId, role });
  const order: Record<string, number> = { producer: 0, member: 1, contractor: 2 };

  if (inviting) return <InviteDialog projectId={space.id} onClose={() => setInviting(false)} />;
  return (
    <Dialog title={`Team on ${space.name}`} onClose={onClose} wide>
      <div className="form">
        <div className="row-gap">
          <MenuButton
            label="Add someone who is already on melo.build"
            className="btn"
            searchable
            width={320}
            items={() =>
              outside.map((p) => ({
                id: p.id,
                label: `${p.name}${p.disciplines.length ? ` · ${p.disciplines.map((d) => DISCIPLINE_LABELS[d] ?? d).join(", ")}` : ""}`,
                icon: <Avatar actor={p} size="sm" />,
                run: () => {
                  setRole(p.id, p.role === "contractor" ? "contractor" : "member");
                  toast(`Added ${p.name}`);
                },
              }))
            }
          >
            <Icon name="plus" /> Add person
          </MenuButton>
          <button type="button" className="btn ghost" onClick={() => setInviting(true)}>
            <Icon name="link" /> Invite by link
          </button>
        </div>
        <div className="subs">
          {members
            .slice()
            .sort((a, b) => order[a.role]! - order[b.role]! || (t.actors.get(a.actorId)?.name ?? "").localeCompare(t.actors.get(b.actorId)?.name ?? ""))
            .map((m) => {
              const p = t.actors.get(m.actorId);
              if (!p) return null;
              const contractor = p.role === "contractor";
              return (
                <div key={m.actorId} className="s team-row">
                  <Avatar actor={p} size="sm" />
                  <span className="grow">
                    {p.name}
                    <span className="muted"> {p.disciplines.map((d) => DISCIPLINE_LABELS[d] ?? d).join(", ")}</span>
                  </span>
                  {contractor ? (
                    <span className="chip" title={ROLE_HINT.contractor}>
                      Contractor
                    </span>
                  ) : (
                    <MenuButton
                      label={`Role for ${p.name}`}
                      className="chip"
                      align="end"
                      items={() => (["producer", "member"] as const).map((r) => ({ id: r, label: ROLE_LABEL[r]!, hint: undefined, checked: m.role === r, run: () => m.role !== r && setRole(p.id, r) }))}
                    >
                      {ROLE_LABEL[m.role]}
                    </MenuButton>
                  )}
                  <button type="button" className="btn ghost icon sm" aria-label={`Remove ${p.name} from ${space.name}`} onClick={() => (setRole(p.id, null), toast(`Removed ${p.name} from ${space.name}`, { action: { label: "Undo", run: () => setRole(p.id, m.role) } }))}>
                    <Icon name="x" />
                  </button>
                </div>
              );
            })}
          {!members.length && <div className="s muted">No one yet. Add people or send an invite link.</div>}
        </div>
        {admins.length > 0 && (
          <p className="hint">
            Admins see every project: {admins.map((a) => a.name).join(", ")}.{isAdmin() ? "" : " Ask one to change workspace roles."}
          </p>
        )}
      </div>
    </Dialog>
  );
}
